<template>
  <el-dialog
    v-model="dialogVisible"
    title="生成解决方案"
    width="1100px"
    class="solution-library-dialog"
    @close="closeDialog"
  >
    <!-- 弹窗内容 -->
    <div class="dialog-content">
      <!-- 技术路径列表 -->
      <div class="section">
        <h3 class="section-title">技术路径</h3>
        <div class="section-content">
          <!-- 没有技术路径数据时的提示 -->
          <div class="no-data" v-if="props.techPaths.length === 0">
            <el-empty description="暂无技术路径数据" />
          </div>
          
          <!-- 技术路径列表 -->
          <div 
            v-for="(path, index) in props.techPaths" 
            :key="index"
            class="item"
          >
            <div class="item-header">
              <el-checkbox v-model="selectedItems[index]" class="item-checkbox" />
              <div class="item-title">{{ path.title }}</div>
              <el-icon class="item-arrow"><ArrowRight /></el-icon>
            </div>
            <div class="item-description">
              {{ path.description }}
            </div>
            <div class="item-progress">
              <div class="progress-label">权重配置</div>
              <!-- 使用滑块组件，允许手动调整权重 -->
              <el-slider
                v-model="path.progress"
                :min="0"
                :max="100"
                :step="5"
                :show-input="false"
                :color="sliderColor"
                class="slider-bar"
              />
              <div class="progress-value">({{ path.progress || 50 }}%)</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 相关专利 -->
      <div class="section">
        <h3 class="section-title">参考专利</h3>
        <div class="section-content">
          <!-- 没有专利数据时的提示 -->
          <div class="no-data" v-if="addedPatents.length === 0">
            <el-empty description="暂无参考专利数据" />
          </div>
          
          <!-- 专利列表：只显示已加入库的专利 -->
          <div 
            v-for="(patent, index) in addedPatents" 
            :key="patent.id !== undefined && patent.id !== null ? 'patent-' + patent.id : 'patent-idx-' + index"
            class="item"
          >
            <div class="item-header">
              <el-checkbox v-model="selectedPatents[index]" class="item-checkbox" />
              <div class="item-title">{{ patent.title || '暂无专利标题' }}</div>
              <el-icon class="item-arrow"><ArrowRight /></el-icon>
            </div>
            <div class="patent-info">
              <span class="patent-number">专利号：{{ patent.p || '暂无专利号' }}</span>
              <span class="patent-note">备注：{{ patent.abstract || '暂无专利摘要' }}</span>
            </div>
            <div class="item-progress">
              <div class="progress-label">权重配置</div>
              <!-- 使用滑块组件，允许手动调整权重 -->
              <el-slider
                v-model="patent.progress"
                :min="0"
                :max="100"
                :step="5"
                :show-input="false"
                :color="sliderColor"
                class="slider-bar"
              />
              <div class="progress-value">({{ patent.progress || 50 }}%)</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 最终问题三元组 -->
      <div class="section">
        <div class="section-header">
          <h3 class="section-title">最终问题三元组 (对象-作用-结果)</h3>
          <el-button type="text" size="small" class="edit-btn" @click="isEditing ? saveEdit() : toggleEditMode()">
            <el-icon v-if="!isEditing"><Edit /></el-icon>
            <el-icon v-else><Check /></el-icon>
            {{ isEditing ? '保存' : '编辑' }}
          </el-button>
        </div>
        <div class="section-content">
          <div class="triplet-items">
            <div class="triplet-item">
              <div class="triplet-label">对象 (What)</div>
              <div v-if="!isEditing" class="triplet-content">{{ tripletData[0] }}</div>
              <el-input 
                v-else 
                v-model="tripletData[0]" 
                placeholder="请输入对象"
                class="triplet-input"
              />
            </div>
            <div class="triplet-item">
              <div class="triplet-label">作用 (Action)</div>
              <div v-if="!isEditing" class="triplet-content">{{ tripletData[1] }}</div>
              <el-input 
                v-else 
                v-model="tripletData[1]" 
                placeholder="请输入对象"
                class="triplet-input"
              />
            </div>
            <div class="triplet-item">
              <div class="triplet-label">结果 (Outcome)</div>
              <div v-if="!isEditing" class="triplet-content">{{ tripletData[2] }}</div>
              <el-input 
                v-else 
                v-model="tripletData[2]" 
                placeholder="请输入对象"
                class="triplet-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 弹窗底部 -->
    <template #footer>
      <div class="dialog-footer">
        <div class="footer-left">
          <!-- <el-checkbox v-model="confirmInfo">
            <el-icon><Check /></el-icon>
            已确认全部来源信息
          </el-checkbox> -->
        </div>
        <div class="footer-right">
          <el-button type="primary" @click="generateSolution">生成解决方案</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, onMounted, computed } from 'vue'
import { ArrowRight, Check, Edit } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { getSelectionDetailByProject } from '@/api/persistence'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  techPaths: {
    type: Array,
    default: () => []
  },
  patents: {
    type: Array,
    default: () => []
  },
  // 最终问题三元组
  triplet: {
    type: Object,
    default: null
  },
  // 项目ID：从数据库读已入库专利
  projectId: {
    type: [String, Number],
    default: null
  }
})

// 定义组件发射的事件
const emit = defineEmits(['update:visible', 'generate-solution', 'close', 'triplet-updated'])

// 内部可见性状态
const dialogVisible = ref(props.visible)

// 确认信息复选框
const confirmInfo = ref(false)

// 选中的技术路径 - 动态初始化
const selectedItems = ref([])

// 选中的专利 - 动态初始化
const selectedPatents = ref([])

// 编辑模式状态
const isEditing = ref(false)




// 最终问题三元组数据
const tripletData = ref([])
watch(() => props.triplet, (newTriplet) => {
  console.log('newTriplet', newTriplet)
  if (newTriplet && newTriplet[0]) {
    if (newTriplet[0].what && newTriplet[0].action && newTriplet[0].outcome) {
      tripletData.value = [newTriplet[0].what, newTriplet[0].action, newTriplet[0].outcome]
    } else {
      tripletData.value = []
    }
  } else {
    tripletData.value = []
  }
})

// 监听techPaths变化：只补默认权重值，不重置勾选状态
// （拖动权重滑块会触发深层变更，若在这里重置 selectedItems 会把用户取消勾选的项目重新勾上）
watch(() => props.techPaths, (newTechPaths) => {
  newTechPaths.forEach(path => {
    if (path.progress === undefined || path.progress === null) {
      path.progress = 50
    }
  })
}, { immediate: true, deep: true })

// 监听props.patents变化：只补默认权重值，不重置勾选状态
watch(() => props.patents, (newPatents) => {
  newPatents.forEach(patent => {
    if (patent.progress === undefined || patent.progress === null) {
      patent.progress = 50
    }
  })
}, { immediate: true, deep: true })

// 切换编辑模式
const toggleEditMode = () => {
  isEditing.value = !isEditing.value
}

// 保存编辑内容
const saveEdit = () => {
  // 这里可以添加保存逻辑，比如发送请求到后端
  isEditing.value = false
  // 发射三元组更新事件
  emit('triplet-updated', tripletData.value)
}

// 监听外部visible prop变化
watch(() => props.visible, (newVal) => {
  dialogVisible.value = newVal
  if (newVal) {
    // 打开弹窗：勾选重置为全选（避免沿用上一次的勾选状态）
    selectedItems.value = new Array(props.techPaths.length).fill(true)
    selectedPatents.value = new Array(addedPatents.value.length).fill(true)
  }
})

// 监听内部dialogVisible变化
watch(dialogVisible, (newVal) => {
  emit('update:visible', newVal)
})

// 组件挂载时初始化选中状态
onMounted(() => {
  // 初始化选中状态，默认全选
  selectedItems.value = new Array(props.techPaths.length).fill(true)
  // 初始化专利选中状态，默认全选
  selectedPatents.value = new Array(props.patents.length).fill(true)
})

// 关闭弹窗
const closeDialog = () => {
  dialogVisible.value = false
  emit('close')
}

// 过滤出已加入库的专利：优先用数据库里的（刷新/切路径不丢），合并当前查询结果中已标记的
const addedPatents = ref([])
let libraryLoaded = false

// 从数据库加载该项目已勾选的专利（alternative_solution type=2）
const loadLibraryPatents = async () => {
  if (!props.projectId) {
    // 无 projectId 时退回旧的过滤逻辑
    addedPatents.value = props.patents.filter(p => p.isAddedToLibrary)
    return
  }
  try {
    const dbList = await getSelectionDetailByProject(props.projectId)
    const selectedPatents = Array.isArray(dbList) ? dbList.filter(s => s.type === 2) : []
    if (selectedPatents.length > 0) {
      // 勾选记录转成前端专利格式
      const dbPatents = selectedPatents.map(item => ({
        id: item.externalId,
        pn: item.externalId,
        p: item.externalId,
        t: item.title,
        title: item.title,
        abstract: item.abstract,
        externalId: item.externalId,
        isAddedToLibrary: true,
        _dbId: Number(item.referId),
        progress: item.progress ?? 50
      }))
      // 合并：数据库为主，当前查询结果里有但库里没有的（刚加入的）也保留
      const merged = [...dbPatents]
      props.patents.forEach(p => {
        if (p.isAddedToLibrary && !merged.some(m => m.externalId === (p.externalId || p.p || p.pn))) {
          merged.push({ ...p, progress: p.progress ?? 50 })
        }
      })
      addedPatents.value = merged
    } else {
      addedPatents.value = props.patents.filter(p => p.isAddedToLibrary)
    }
    libraryLoaded = true
  } catch (e) {
    console.error('加载已勾选专利失败:', e)
    addedPatents.value = props.patents.filter(p => p.isAddedToLibrary)
  }
}

// 弹窗打开时从数据库加载已入库专利
watch(() => props.visible, (newVal) => {
  dialogVisible.value = newVal
  if (newVal) {
    libraryLoaded = false
    void loadLibraryPatents()
  }
})

// 监听 addedPatents 变化：仅当列表整体替换（换数据/打开弹窗加载）时重置勾选为全选；
// 不深度监听，避免专利对象内部修改（如拖动权重滑块）把取消勾选的项目重新勾上
watch(
  addedPatents,
  (newAddedPatents) => {
    selectedPatents.value = new Array(newAddedPatents.length).fill(true)
  }
)

// 生成解决方案
const generateSolution = () => {
  // 调试日志
  console.log('generateSolution called')
  console.log('selectedItems:', selectedItems.value)
  console.log('selectedPatents:', selectedPatents.value)
  console.log('props.techPaths:', props.techPaths)
  console.log('addedPatents:', addedPatents.value)
  
  // 检查是否有技术路径被选中
  const hasSelectedPath = selectedItems.value.some(selected => selected)
  
  console.log('hasSelectedPath:', hasSelectedPath)
  
  if (!hasSelectedPath) {
    ElMessage.warning('请至少选择一个技术路径')
    return
  }
  
  // 筛选出选中的技术路径
  const selectedTechPaths = props.techPaths.filter((_, index) => selectedItems.value[index])
  // 筛选出选中的专利
  const selectedPatentsData = addedPatents.value.filter((_, index) => selectedPatents.value[index])
  
  console.log('selectedTechPaths:', selectedTechPaths)
  console.log('selectedPatentsData:', selectedPatentsData)
  
  // 直接传递选中的全部数据
  emit('generate-solution', {
    selectedTechPaths,
    selectedPatentsData
  })
  closeDialog()
}

// 滑块颜色配置对象
const sliderColor = {
  '0%': '#f56c6c', // 红色
  '50%': '#e6a23c', // 黄色
  '100%': '#52c41a' // 绿色（与AnalysisResult.vue保持一致）
}

// 移除旧的getProgressColors函数，不再需要
</script>

<style scoped lang="scss">
.solution-library-dialog {
  
  :deep(.el-dialog__header) {
    padding: 20px;
    border-bottom: 1px solid #e8e8e8;
    
    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }
  
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 641px;
    overflow-y: auto;
  }
  
  :deep(.el-dialog__footer) {
    padding: 15px 20px;
    border-top: 1px solid #e8e8e8;
  }
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 通用样式 */
.section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  position: relative;
  padding-left: 16px;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 16px;
    background-color: #1890ff;
  }
}

.section-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 技术路径和专利项 */
.item {
  background-color: transparent;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
}

.item-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  
  .item-checkbox {
    margin-right: 12px;
    
    :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
      background-color: #1890ff;
      border-color: #1890ff;
    }
    
    :deep(.el-checkbox__input.is-checked+.el-checkbox__label) {
      color: #333;
    }
  }
  
  .item-title {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-right: 8px;
  }
  
  .item-arrow {
    color: #999;
    font-size: 14px;
  }
}

.item-description {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 12px;
  margin-left: 36px;
}

.item-progress {
  display: flex;
  align-items: center;
  margin-left: 36px;
  gap: 12px;
  
  .progress-label {
    font-size: 14px;
    color: #666;
    min-width: 80px;
  }
  
  .slider-bar {
    flex: 1;
    margin-right: 12px;
    
    :deep(.el-slider__runway) {
      background-color: #f0f0f0;
      border-radius: 4px;
      height: 8px;
    }
    
    :deep(.el-slider__bar) {
      border-radius: 4px;
      height: 8px;
    }
    
    :deep(.el-slider__button) {
      width: 16px;
      height: 16px;
      background-color: currentColor;
      border-color: currentColor;
    }
    
    :deep(.el-slider__button:hover),
    :deep(.el-slider__button:focus) {
      background-color: currentColor;
      border-color: currentColor;
      box-shadow: 0 0 0 5px rgba(24, 144, 255, 0.1);
    }
  }
  
  .progress-value {
    font-size: 14px;
    color: #666;
    min-width: 50px;
  }
}

/* 专利信息 */
.patent-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  margin-left: 36px;
  
  .patent-number,
  .patent-note {
    font-size: 14px;
    color: #666;
  }
}

/* 最终问题三元组 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.edit-btn {
  padding: 0;
  font-size: 14px;
  color: #1890ff;
  
  :deep(.el-icon) {
    margin-right: 4px;
  }
  
  &:hover {
    color: #40a9ff;
  }
}

.triplet-items {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.triplet-item {
  flex: 1;
  min-width: 300px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  
  /* 移除编辑模式下的输入框与triplet-item之间的双重边框视觉效果 */
  :deep(.el-input__wrapper) {
    box-shadow: none;
    border-radius: 4px;
  }
}

.triplet-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
}

.triplet-content {
  font-size: 14px;
  color: #333;
  background-color: #fafafa;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.triplet-input {
  width: 100%;
  
  :deep(.el-input__inner) {
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    padding: 6px 12px;
    font-size: 14px;
    transition: border-color 0.2s;
    
    &:focus {
      border-color: #1890ff;
      outline: none;
    }
  }
}

/* 弹窗底部 */
.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.footer-left {
  
  :deep(.el-checkbox) {
    font-size: 14px;
    color: #666;
    
    .el-checkbox__label {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    :deep(.el-icon) {
      color: #1890ff;
    }
  }
}

.footer-right {
  
  :deep(.el-button--primary) {
    padding: 8px 16px;
    background-color: #1890ff;
    border-color: #1890ff;
    color: #fff;
    
    &:hover {
      background-color: #40a9ff;
      border-color: #40a9ff;
    }
  }
}

/* 复选框样式 */
.item-checkbox {
  :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-color: #1890ff;
    border-color: #1890ff;
  }
  
  :deep(.el-checkbox__inner) {
    width: 16px;
    height: 16px;
  }
}
</style>